import { useState } from "react";
import { Input } from "../components/ui/input";
import { PageHeader, Section } from "../components/AiHtml";
import { useAuth } from "../context/AuthContext";

const Field = ({ label, value, onChange, testId }) => (
  <label className="space-y-1.5 block"><span className="text-xs text-slate-400">{label}</span><Input data-testid={testId} type="number" value={value} onChange={(e) => onChange(Number(e.target.value))} /></label>
);
const inr = (n) => `₹${Math.round(n).toLocaleString("en-IN")}`;

const PlanMargins = ({ plans }) => {
  const [costInr, setCostInr] = useState(0.85);
  const [usage, setUsage] = useState(70);
  return (
    <Section title="Owner view · plan margins (admin only)" testId="plan-margins">
      <div className="flex flex-wrap gap-4 mb-4">
        <Field testId="margin-cost-per-credit" label="Worst-case AI cost per credit (₹)" value={costInr} onChange={setCostInr} />
        <Field testId="margin-usage" label="Avg % of credits actually used" value={usage} onChange={setUsage} />
      </div>
      <div className="overflow-x-auto"><table className="w-full text-sm min-w-[620px]"><thead><tr className="text-left text-xs font-mono uppercase text-slate-500"><th className="py-2">Plan</th><th>Price</th><th>Credits</th><th>AI cost</th><th>Gateway 2-3%</th><th>Profit</th><th>Margin</th></tr></thead>
        <tbody>{Object.entries(plans).map(([n, p]) => {
          const cost = p.credits * costInr * (usage / 100);
          const fee = p.inr * 0.024;
          const profit = p.inr - cost - fee;
          return <tr key={n} className="border-t border-slate-800"><td className="py-2">{n}</td><td>{inr(p.inr)} / ${p.usd}</td><td>{p.credits.toLocaleString()}</td><td>{inr(cost)}</td><td>{inr(fee)}</td><td className="text-emerald-400">{inr(profit)}</td><td className="text-orange-400 font-mono">{((profit / p.inr) * 100).toFixed(0)}%</td></tr>;
        })}</tbody></table></div>
      <p className="text-xs text-slate-500 mt-3">USD plans are priced ~20-25% higher than INR, so international margins are higher. Excludes fixed hosting.</p>
    </Section>
  );
};

export default function Pricing() {
  const { config, user } = useAuth();
  const [retainer, setRetainer] = useState(40000);
  const [clients, setClients] = useState(10);
  const [creditsPerClient, setCpc] = useState(2000);
  const [creditCost, setCreditCost] = useState(3.5);
  const [humanCost, setHumanCost] = useState(35000);
  const [humans, setHumans] = useState(10);

  const revenue = retainer * clients;
  const aiCost = creditsPerClient * clients * creditCost;
  const humanTotal = humanCost * humans;
  const profit = revenue - aiCost;
  const margin = revenue ? (profit / revenue) * 100 : 0;
  const saved = humanTotal - aiCost;

  return (
    <div className="space-y-6">
      <PageHeader label="Pricing & Margins" title="Agency profit calculator" desc="See how replacing a 10-person team with AI employees changes your margins." />
      {user?.isAdmin && config && <PlanMargins plans={config.plans} />}
      <div className="grid lg:grid-cols-3 gap-6">
        <Section title="Inputs">
          <div className="space-y-4">
            <Field testId="calc-retainer" label="Client retainer / month (₹)" value={retainer} onChange={setRetainer} />
            <Field testId="calc-clients" label="Number of clients" value={clients} onChange={setClients} />
            <Field testId="calc-credits" label="Credits used per client / month" value={creditsPerClient} onChange={setCpc} />
            <Field testId="calc-credit-cost" label="Your cost per credit (₹)" value={creditCost} onChange={setCreditCost} />
            <Field testId="calc-human-cost" label="Avg human salary / month (₹)" value={humanCost} onChange={setHumanCost} />
            <Field testId="calc-humans" label="Employees replaced" value={humans} onChange={setHumans} />
          </div>
        </Section>
        <div className="lg:col-span-2 space-y-6">
          <div className="grid sm:grid-cols-2 gap-4">
            {[["Monthly revenue", inr(revenue), "calc-out-revenue"], ["AI cost", inr(aiCost), "calc-out-aicost"], ["Net profit", inr(profit), "calc-out-profit"], ["Saved vs human team", inr(saved), "calc-out-saved"]].map(([l, v, t]) => (
              <div key={l} className="card-n p-5"><p className="label-mono">{l}</p><p data-testid={t} className="font-display text-3xl font-bold mt-2">{v}</p></div>
            ))}
          </div>
          <Section title="Net profit margin">
            <div className="h-4 rounded-full bg-slate-800 overflow-hidden"><div className="h-full bg-gradient-to-r from-orange-600 to-orange-400 transition-[width] duration-500" style={{ width: `${Math.max(0, Math.min(100, margin))}%` }} /></div>
            <p data-testid="calc-out-margin" className="font-display text-4xl font-bold mt-3 text-orange-400">{margin.toFixed(1)}%</p>
            <p className="text-sm text-slate-400 mt-1">Human team cost would be {inr(humanTotal)} / month.</p>
          </Section>
          <Section title="Credit costs per task">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-sm">
              {Object.entries(config?.costs || {}).map(([k, v]) => <div key={k} className="rounded bg-slate-900 border border-slate-800 px-3 py-2 flex justify-between"><span className="text-slate-400 capitalize">{k.replace("_", " ")}</span><span className="font-mono text-orange-400">{v}</span></div>)}
            </div>
          </Section>
        </div>
      </div>
    </div>
  );
}
